import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Repeat, Trophy, ArrowRight, Users, Timer, BarChart3, WifiOff, Calendar, MapPin, ExternalLink, Share2, Copy, Play, Plus, CheckCircle2, ShieldCheck, Swords, UserCircle, Database, ClipboardList, Search, Inbox, ChevronDown, ChevronUp } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import MessageAdmin from "@/components/MessageAdmin";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/tournament";
import { hydrateState, resetState } from "@/lib/openPlayStore";
import api from "@/lib/api";

function fmtDuration(s) {
  if (!s) return "0m";
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch (e) { return ""; }
}

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canOpenPlay = hasCapability(user, "op_queue");
  const canDirectory = hasCapability(user, "player_directory");
  const canClubWars = hasCapability(user, "cw_event");
  const canDatabase = hasCapability(user, "player_database");
  const isPlayer = user?.account_type === "player";
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [shareEvent, setShareEvent] = useState(null);
  const [eventsCollapsed, setEventsCollapsed] = useState(() => localStorage.getItem("pc_dash_events_collapsed") === "1");

  const toggleEventsCollapsed = () => {
    setEventsCollapsed((c) => {
      const next = !c;
      localStorage.setItem("pc_dash_events_collapsed", next ? "1" : "0");
      return next;
    });
  };

  useEffect(() => {
    if (isPlayer) return;
    api.get("/openplay/stats").then(({ data }) => setStats(data)).catch(() => setStats(false));
    api.get("/openplay/events").then(({ data }) => setEvents(data || [])).catch(() => setEvents([]));
  }, [isPlayer]);

  const continueEvent = async (token) => {
    try {
      const { data } = await api.get(`/openplay/resume/${token}`);
      if (data && data.players) {
        hydrateState(data);
        nav("/open-play");
      } else {
        toast.error("No saved session to resume for this event.");
      }
    } catch (e) {
      toast.error("Could not resume this event.");
    }
  };

  const startNewSession = () => {
    resetState();
    nav("/open-play");
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Modules" />
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-10 grain">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Welcome back, {user?.name?.split(" ")[0] || (isPlayer ? "Player" : "Organizer")}</span>
            <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-2">{isPlayer ? "Your tournaments." : "Choose your module."}</h1>
          </div>
          <div className="flex items-center flex-wrap gap-2">
              <button data-testid="dashboard-umpire-console" onClick={() => nav("/umpire/score")}
                className="glass rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}>
                <ShieldCheck className="w-4 h-4 text-primary-c" /> Umpire Console
              </button>
            )}
            {!isPlayer && canDirectory && (
              <button data-testid="dashboard-players" onClick={() => nav("/players")}
                className="glass rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}>
                <Users className="w-4 h-4 text-primary-c" /> Players
              </button>
            )}
            <button data-testid="dashboard-profile" onClick={() => nav("/profile")}
              className="glass rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}>
              <Users className="w-4 h-4 text-primary-c" /> Profile
            </button>
            <button data-testid="dashboard-my-tournaments" onClick={() => nav("/my-tournaments")}
              className="glass rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}>
              <Trophy className="w-4 h-4 text-primary-c" /> My Tournaments
            </button>
            {!isPlayer && canOpenPlay && (
              <button data-testid="dashboard-new-session-button" onClick={startNewSession}
                className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2">
                <Plus className="w-4 h-4" /> New Session
              </button>
            )}
          </div>
        </motion.div>

        <SubscriptionBanner className="mt-8" />

        <div className={`grid ${isPlayer ? "md:grid-cols-1 max-w-2xl" : "md:grid-cols-2"} gap-6 mt-10`}>
          {!isPlayer && canOpenPlay && (
          <motion.button
            data-testid="module-open-play" onClick={() => nav("/open-play")}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
            className="group relative overflow-hidden rounded-3xl glow-border grain p-8 text-left hover:scale-[1.01] transition-transform"
            style={{ background: "var(--surface)" }}
          >
            <img src="https://images.unsplash.com/photo-1692288720754-743fbd1f2155?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.14 }} />
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl glass flex items-center justify-center mb-6 pulse-ring">
                <Repeat className="w-7 h-7 text-primary-c" />
              </div>
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Module 1 · Live</span>
              <h2 className="font-display font-bold text-2xl mt-2">Open-Play Queue Manager</h2>
              <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>
                FIFO rotations, sit-to-play fairness engine and offline-first courts.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-bold text-primary-c group-hover:gap-3 transition-all">
                Launch module <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </motion.button>
          )}

          <motion.button
            data-testid="module-tournament" onClick={() => nav(isPlayer ? "/my-tournaments" : "/tournaments")}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12 }}
            className="group relative overflow-hidden rounded-3xl glow-border grain p-8 text-left hover:scale-[1.01] transition-transform"
            style={{ background: "var(--surface)" }}
          >
            <div className="w-14 h-14 rounded-2xl glass flex items-center justify-center mb-6 pulse-ring">
              <Trophy className="w-7 h-7 text-primary-c" />
            </div>
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Module 2 · Live</span>
            <h2 className="font-display font-bold text-2xl mt-2">{isPlayer ? "Tournament Registration" : "Pro Tournament Suite"}</h2>
            <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>
              {isPlayer ? "View your registrations, accept partner invites and upload payment proof." : "Singles & doubles brackets, umpire mobile scoring and immutable audit logs."}
            </p>
            <span className="mt-6 inline-flex items-center gap-2 font-bold text-primary-c group-hover:gap-3 transition-all">
              {isPlayer ? "Open" : "Launch module"} <ArrowRight className="w-4 h-4" />
            </span>
          </motion.button>
        </div>

        {!isPlayer && canClubWars && (
        <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mt-6">
          <motion.button
            data-testid="module-club-wars" onClick={() => nav("/club-wars")}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}
            className="group relative overflow-hidden rounded-3xl glow-border grain p-8 text-left hover:scale-[1.01] transition-transform"
            style={{ background: "var(--surface)" }}
          >
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl glass flex items-center justify-center mb-6 pulse-ring">
                <Swords className="w-7 h-7 text-primary-c" />
              </div>
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Module 3 · Team vs Team</span>
              <h2 className="font-display font-bold text-2xl mt-2">Club Wars / Dual Meets</h2>
              <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>
                Structured club-vs-club showdowns — teams, line-ups, weighted scoring and live team leaderboards.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-bold text-primary-c group-hover:gap-3 transition-all">
                Launch module <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </motion.button>
        </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="ratings-tiles">
          {[["/players/me", UserCircle, "My Player Profile", "Ratings, match history & community rating requests", "tile-my-profile"],
            ...(isPlayer ? [["/find-players", Search, "Find & Rate Players", "Search by name, email or phone and rate players you've played with", "tile-find-players"],
              ["/inbox", Inbox, "Inbox", "Messages from organizers of your events", "tile-inbox"]] : []),
            ...(!isPlayer && canDatabase ? [["/player-database", Database, "Player Database", "Search players by Official, Assessment & Composite rating", "tile-player-database"]] : []),
            ...(!isPlayer ? [["/organizer/ratings", ClipboardList, "Rate Participants", "Rate players from your events, incl. external events", "tile-organizer-ratings"]] : [])
          ].map(([to, Icon, t, sub, tid]) => (
            <button key={to} data-testid={tid} onClick={() => nav(to)} className="group rounded-2xl glow-border p-5 text-left hover:scale-[1.01] transition-transform flex gap-3" style={{ background: "var(--surface)" }}>
              <div className="w-11 h-11 rounded-xl glass flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-primary-c" /></div>
              <div><div className="font-display font-bold">{t}</div><div className="text-xs mt-0.5" style={{ color: "var(--text-2)" }}>{sub}</div></div>
            </button>
          ))}
        </div>

        {!isPlayer && (
        <>
        {/* Online stats */}
        <div className="mt-12">
          <div className="flex items-center gap-2 mb-5">
            <BarChart3 className="w-5 h-5 text-primary-c" />
            <h3 className="font-display font-bold text-xl">Cloud Stats</h3>
            <span className="text-xs mono" style={{ color: "var(--text-2)" }}>synced across sessions</span>
          </div>

          {stats === false && (
            <div className="rounded-2xl glow-border p-6 flex items-center gap-3 text-sm" style={{ background: "var(--surface)", color: "var(--text-2)" }}>
              <WifiOff className="w-4 h-4" /> Offline — cloud stats unavailable. Your courts still run locally.
            </div>
          )}

          {stats && stats !== false && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Trophy} label="Matches logged" value={stats.total_matches} testid="stat-matches" />
                <StatCard icon={Users} label="Players tracked" value={stats.unique_players} testid="stat-players" />
                <StatCard icon={Timer} label="Total play time" value={fmtDuration(stats.total_play_seconds)} testid="stat-time" />
                <StatCard icon={BarChart3} label="Top player games" value={stats.leaderboard?.[0]?.games ?? 0} testid="stat-top" />
              </div>

              {stats.leaderboard?.length > 0 && (
                <div className="mt-6 rounded-2xl glow-border p-6" style={{ background: "var(--surface)" }} data-testid="leaderboard">
                  <h4 className="font-display font-bold mb-4">Leaderboard</h4>
                  <div className="space-y-2">
                    {stats.leaderboard.map((p, i) => (
                      <div key={p.name} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0" style={{ borderColor: "var(--border)" }}>
                        <span className="flex items-center gap-3">
                          <span className="mono text-primary-c w-6">#{i + 1}</span>{p.name}
                        </span>
                        <span className="mono font-bold flex items-center gap-3">
                          {typeof p.wins === "number" && <span className="text-primary-c">{p.wins}W</span>}
                          <span>{p.games}g</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* events hosted */}
        {events.length > 0 && (
          <div className="mt-12" data-testid="events-section">
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              <Calendar className="w-5 h-5 text-primary-c" />
              <h3 className="font-display font-bold text-xl">Your Events</h3>
              <span className="text-xs mono glass rounded-full px-2 py-0.5" style={{ color: "var(--text-2)" }} data-testid="events-count">{events.length}</span>
              <span className="text-xs mono hidden sm:inline" style={{ color: "var(--text-2)" }}>review details, rankings & share</span>
              <button data-testid="events-toggle-collapse" onClick={toggleEventsCollapsed}
                className="ml-auto glass rounded-full px-3.5 py-1.5 text-xs font-bold flex items-center gap-1.5 hover:scale-105 transition-transform" style={{ color: "var(--text)" }}>
                {eventsCollapsed ? <><ChevronDown className="w-3.5 h-3.5 text-primary-c" /> Show all</> : <><ChevronUp className="w-3.5 h-3.5 text-primary-c" /> Minimize all</>}
              </button>
            </div>
            {!eventsCollapsed && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="events-list">
                {events.map((ev) => (
                <div key={ev.token} data-testid={`event-card-${ev.token}`} className="rounded-2xl glow-border p-5 flex flex-col" style={{ background: "var(--surface)" }}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-display font-bold truncate">{ev.event_name || "Open Play"}</div>
                    {ev.ended && (
                      <span data-testid={`event-ended-badge-${ev.token}`} className="mono text-[10px] uppercase tracking-wider glass rounded-full px-2 py-0.5 shrink-0 flex items-center gap-1" style={{ color: "var(--text-2)" }}>
                        <CheckCircle2 className="w-3 h-3 text-primary-c" /> Ended
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs" style={{ color: "var(--text-2)" }}>
                    {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{ev.location}</span>}
                    {ev.mode && <span className="capitalize">{ev.mode}</span>}
                    <span>{fmtDate(ev.updated_at)}</span>
                  </div>
                  <div className="flex gap-4 mt-3 text-sm">
                    <span className="mono"><span className="text-primary-c font-bold">{ev.matches ?? 0}</span> matches</span>
                    <span className="mono"><span className="text-primary-c font-bold">{ev.players ?? 0}</span> players</span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    {ev.ended ? (
                      <a href={`${window.location.origin}/live/${ev.token}`} target="_blank" rel="noreferrer"
                        data-testid={`event-result-${ev.token}`}
                        className="flex-1 btn-glow rounded-full py-2 text-sm font-bold flex items-center justify-center gap-2">
                        <Trophy className="w-4 h-4" /> View Result
                      </a>
                    ) : (
                      <button data-testid={`event-continue-${ev.token}`} onClick={() => continueEvent(ev.token)}
                        className="flex-1 btn-glow rounded-full py-2 text-sm font-bold flex items-center justify-center gap-2">
                        <Play className="w-4 h-4" /> Continue
                      </button>
                    )}
                    <a href={`${window.location.origin}/live/${ev.token}`} target="_blank" rel="noreferrer"
                      data-testid={`event-view-${ev.token}`} className="glass rounded-full px-3 flex items-center justify-center hover:scale-105 transition-transform" aria-label="View & ranking">
                      <ExternalLink className="w-4 h-4 text-primary-c" />
                    </a>
                    <button data-testid={`event-share-${ev.token}`} onClick={() => setShareEvent(ev)}
                      aria-label="Share event" className="glass rounded-full w-10 flex items-center justify-center hover:scale-105 transition-transform">
                      <Share2 className="w-4 h-4 text-primary-c" />
                    </button>
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        )}
        </>
        )}

        {/* message the team */}
        <div className="mt-12 max-w-2xl" data-testid="dashboard-message-section">
          <MessageAdmin />
        </div>
      </div>

      {/* share event ranking modal */}
      <Dialog open={!!shareEvent} onOpenChange={(o) => !o && setShareEvent(null)}>
        <DialogContent data-testid="event-share-modal" className="glass" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><Share2 className="w-5 h-5 text-primary-c" /> Share {shareEvent?.event_name || "event"}</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>Anyone can scan to view this event's ranking and results.</DialogDescription>
          </DialogHeader>
          {shareEvent && (
            <div className="flex flex-col items-center gap-4">
              <div className="bg-white p-3 rounded-xl" data-testid="event-share-qr">
                <QRCodeCanvas value={`${window.location.origin}/live/${shareEvent.token}`} size={168} includeMargin={false} />
              </div>
              <div className="w-full flex items-center gap-2">
                <input readOnly data-testid="event-share-link" value={`${window.location.origin}/live/${shareEvent.token}`}
                  className="flex-1 rounded-lg bg-surface glow-border px-3 py-2 text-xs mono truncate" style={{ color: "var(--text)" }} />
                <button data-testid="event-copy-link"
                  onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/live/${shareEvent.token}`); toast.success("Link copied"); }}
                  className="btn-glow rounded-lg px-3 py-2 font-bold flex items-center gap-1 shrink-0"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}>
      <Icon className="w-5 h-5 text-primary-c" />
      <div className="font-display font-black text-3xl tracking-tighter mt-3">{value}</div>
      <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>{label}</div>
    </div>
  );
}
